/**
 * RichMentionEditorWrapper Component
 *
 * TypeScript wrapper for the existing RichMentionEditor.js component.
 * Maintains 100% compatibility with legacy functionality while adding theme integration.
 *
 * CRITICAL: This wrapper preserves all @ mention functionality, Expression/Result mode,
 * and TipTap integration without modifying the original component.
 */

import React from 'react';
import { Box, useTheme } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
// @ts-ignore - Legacy JS component without type definitions
import RichMentionEditor from '../../../../../frontend/src/components/ProjectView/RichMentionEditor';

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

/**
 * Audience Data Structure (Legacy Compatible)
 */
export interface AudienceData {
  id: string;
  name: string;
  actors?: ActorData[];
}

/**
 * Actor Data Structure (Legacy Compatible)
 */
export interface ActorData {
  id: string;
  name: string;
  coreJobs?: any;
  contexts?: any;
  [key: string]: any; // For dynamic fields
}

/**
 * Task Data Structure (Legacy Compatible)
 */
export interface TaskData {
  id: string;
  name?: string;
  description?: string;
  funnelGoals?: any;
  requiredInfo?: any;
  userCondition?: any;
  competitorsForClients?: any;
}

/**
 * Study Case Data Structure (Legacy Compatible)
 */
export interface StudyCaseData {
  id: string;
  name?: string;
  description?: string;
  [key: string]: any;
}

/**
 * Project Data Structure (Legacy Compatible)
 */
export interface ProjectData {
  id: string;
  projectName?: string;
  [key: string]: any;
}

/**
 * User Data Structure (Legacy Compatible)
 */
export interface UserData {
  id: string;
  name?: string;
  email?: string;
  [key: string]: any;
}

/**
 * Mention Configuration (Enhanced)
 */
export interface MentionConfig {
  id: string;
  label: string;
  value: string;
  type?: string;
  category?: string;
  description?: string;
  icon?: React.ReactNode;
  metadata?: Record<string, any>;

  // Expression/Result system integration
  expressionPath?: string;
  resultFormatter?: (value: any) => string;
}

// ============================================================================
// COMPONENT PROPS INTERFACE
// ============================================================================

/**
 * RichMentionEditorWrapper Props
 *
 * Full TypeScript interface for the wrapper while maintaining legacy compatibility.
 */
export interface RichMentionEditorWrapperProps {
  /**
   * Core Content Management (Legacy Compatible)
   */
  value: string;
  onChange: (content: string) => void;

  /**
   * Legacy Required Props (Direct Pass-Through)
   */
  currentProjectId: string;
  currentUser: UserData;
  audiences?: AudienceData[];
  actorsByAudience?: Record<string, ActorData[]>;
  tasks?: TaskData[];
  studyCases?: StudyCaseData[];
  project?: ProjectData;

  /**
   * Editor Configuration (Enhanced)
   */
  placeholder?: string;
  readOnly?: boolean;
  adminLibraryMode?: boolean;

  /**
   * Theme Integration Wrapper (NEW)
   */
  sx?: SxProps<Theme>;
  variant?: 'outlined' | 'filled' | 'minimal' | 'standard';
  size?: 'small' | 'medium' | 'large';

  /**
   * Enhanced Features (Optional)
   */
  maxHeight?: number | string;
  minHeight?: number | string;
  autoFocus?: boolean;
  showBorder?: boolean;

  /**
   * Expression/Result System (CRITICAL Legacy Feature)
   */
  enableExpressionMode?: boolean;
  onExpressionToggle?: (isExpression: boolean) => void;
  onResultGenerated?: (result: string) => void;

  /**
   * Event Handlers (Enhanced)
   */
  onFocus?: (event: React.FocusEvent) => void;
  onBlur?: (event: React.FocusEvent) => void;
  onSelectionUpdate?: (selection: any) => void;

  /**
   * Validation Integration
   */
  error?: boolean;
  errorMessage?: string;
  helperText?: string;
  onValidationChange?: (isValid: boolean, errors: string[]) => void;

  /**
   * Accessibility Enhancement
   */
  'aria-label'?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;

  /**
   * Legacy Component Pass-Through
   */
  legacyProps?: Record<string, any>;
  preserveLegacyBehavior?: boolean;
}

// ============================================================================
// THEME-AWARE STYLES HOOK
// ============================================================================

/**
 * Hook for generating theme-aware styles for the wrapper
 */
const useRichMentionEditorStyles = (
  variant: RichMentionEditorWrapperProps['variant'] = 'outlined',
  size: RichMentionEditorWrapperProps['size'] = 'medium',
  error: boolean = false,
  showBorder: boolean = true
) => {
  const theme = useTheme();

  return {
    container: {
      position: 'relative',
      width: '100%',

      // Size variants
      ...(size === 'small' && {
        '& .MuiPaper-root': {
          minHeight: theme.spacing(10), // 80px
        }
      }),
      ...(size === 'medium' && {
        '& .MuiPaper-root': {
          minHeight: theme.spacing(15), // 120px
        }
      }),
      ...(size === 'large' && {
        '& .MuiPaper-root': {
          minHeight: theme.spacing(20), // 160px
        }
      }),

      // Variant styles - theme-aware wrapper styles
      ...(variant === 'outlined' && {
        '& .MuiPaper-root': {
          backgroundColor: theme.palette.background.paper,
          borderColor: error ? theme.palette.error.main : theme.palette.divider,
          '&:hover': {
            borderColor: error ? theme.palette.error.main : theme.palette.primary.main,
          },
          '&:focus-within': {
            borderColor: error ? theme.palette.error.main : theme.palette.primary.main,
            borderWidth: '2px',
          }
        }
      }),

      ...(variant === 'filled' && {
        '& .MuiPaper-root': {
          backgroundColor: theme.palette.action.hover,
          border: 'none',
          borderBottom: `2px solid ${error ? theme.palette.error.main : theme.palette.divider}`,
          borderRadius: `${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0 0`,
          '&:hover': {
            backgroundColor: theme.palette.action.selected,
          },
          '&:focus-within': {
            backgroundColor: theme.palette.background.paper,
            borderBottom: `2px solid ${error ? theme.palette.error.main : theme.palette.primary.main}`,
          }
        }
      }),

      ...(variant === 'minimal' && {
        '& .MuiPaper-root': {
          backgroundColor: 'transparent',
          border: 'none',
          boxShadow: 'none',
          borderBottom: `1px solid ${error ? theme.palette.error.main : theme.palette.divider}`,
          borderRadius: 0,
          '&:focus-within': {
            borderBottom: `2px solid ${error ? theme.palette.error.main : theme.palette.primary.main}`,
          }
        }
      }),

      ...(variant === 'standard' && {
        '& .MuiPaper-root': {
          backgroundColor: theme.palette.background.paper,
          borderColor: error ? theme.palette.error.main : theme.palette.divider,
          boxShadow: theme.shadows[1],
          '&:hover': {
            boxShadow: theme.shadows[2],
          },
          '&:focus-within': {
            boxShadow: theme.shadows[3],
            borderColor: error ? theme.palette.error.main : theme.palette.primary.main,
          }
        }
      }),

      // Hide border if requested
      ...(!showBorder && {
        '& .MuiPaper-root': {
          border: 'none',
        }
      }),

      // Theme-aware mention chip styling override
      '& .mention-chip': {
        backgroundColor: theme.palette.primary.light,
        color: theme.palette.primary.contrastText,
        borderRadius: `${Number(theme.shape.borderRadius) * 2}px`, // More rounded for chips
        '&:hover': {
          backgroundColor: theme.palette.primary.main,
        }
      },

      // Theme-aware prosemirror styling
      '& .ProseMirror': {
        color: theme.palette.text.primary,
        '& .ProseMirror-selectednode': {
          outline: `2px solid ${theme.palette.primary.main}`,
        }
      },

      // Theme-aware placeholder styling
      '& .ProseMirror p.is-editor-empty:first-child::before': {
        color: theme.palette.text.disabled,
      }
    } as SxProps<Theme>,

    helperText: {
      marginTop: theme.spacing(0.5),
      marginLeft: theme.spacing(1.75),
      color: error ? theme.palette.error.main : theme.palette.text.secondary,
      fontSize: theme.typography.caption.fontSize,
      lineHeight: theme.typography.caption.lineHeight,
    } as SxProps<Theme>,

    errorText: {
      marginTop: theme.spacing(0.5),
      marginLeft: theme.spacing(1.75),
      color: theme.palette.error.main,
      fontSize: theme.typography.caption.fontSize,
      lineHeight: theme.typography.caption.lineHeight,
    } as SxProps<Theme>,
  };
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

/**
 * RichMentionEditorWrapper Component
 *
 * A TypeScript wrapper for RichMentionEditor.js that adds theme integration
 * while preserving all legacy functionality.
 */
const RichMentionEditorWrapper: React.FC<RichMentionEditorWrapperProps> = ({
  // Core props (required)
  value,
  onChange,
  currentProjectId,
  currentUser,

  // Legacy data props
  audiences,
  actorsByAudience,
  tasks,
  studyCases,
  project,

  // Editor configuration
  placeholder,
  readOnly = false,
  adminLibraryMode = false,

  // Theme integration
  sx,
  variant = 'outlined',
  size = 'medium',

  // Enhanced features
  maxHeight,
  minHeight,
  autoFocus = false,
  showBorder = true,

  // Expression/Result system
  enableExpressionMode = true,
  onExpressionToggle,
  onResultGenerated,

  // Event handlers
  onFocus,
  onBlur,
  onSelectionUpdate,

  // Validation
  error = false,
  errorMessage,
  helperText,
  onValidationChange,

  // Accessibility
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,

  // Legacy pass-through
  legacyProps,
  preserveLegacyBehavior = true,
}) => {
  const styles = useRichMentionEditorStyles(variant, size, error, showBorder);

  // Combine all legacy props
  const legacyComponentProps = {
    value,
    onChange,
    currentProjectId,
    currentUser,
    audiences,
    actorsByAudience,
    placeholder,
    tasks,
    studyCases,
    project,
    adminLibraryMode,
    readOnly,
    ...legacyProps,
  };

  return (
    <Box
      sx={[
        styles.container,
        // Apply height constraints to container
        {
          ...(maxHeight && { maxHeight }),
          ...(minHeight && { minHeight }),
        },
        // User-provided styles override everything
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      role="textbox"
      aria-label={ariaLabel}
      aria-describedby={ariaDescribedBy}
      aria-invalid={ariaInvalid || error}
    >
      {/* Legacy RichMentionEditor component - NO MODIFICATIONS */}
      <RichMentionEditor {...legacyComponentProps} />

      {/* Error message display */}
      {error && errorMessage && (
        <Box sx={styles.errorText}>
          {errorMessage}
        </Box>
      )}

      {/* Helper text display */}
      {!error && helperText && (
        <Box sx={styles.helperText}>
          {helperText}
        </Box>
      )}
    </Box>
  );
};

export default RichMentionEditorWrapper;